// src/components/schoolmate/SchoolmateTemplate.jsx
// Entity-Log Operations Dashboard - Actors • Items • Activities
import React, { useState, useEffect, useMemo } from 'react';
import { 
  School, Users, Box, Calendar, ClipboardList, FileText, LayoutDashboard,
  ChevronRight, ChevronLeft, Menu, X, Search, Plus, MoreHorizontal, Clock,
  User, Paperclip, GraduationCap, Briefcase, UsersRound, Target, Sliders,
  Award, Phone, Grid3X3, TrendingUp, Mail, MapPin, BookOpen, Eye, Compass,
  Heart, Flag, ShieldCheck
} from 'lucide-react';
import { schoolmateService, LogType, ItemStatus, ActivityType, ActivityTypeMeta, ActivityTypeOrder, ActivityStatusMeta } from '../../services/schoolmateService';
import { SchoolmateManager } from '../schoolmate/SchoolmateManager';
import { useAuth } from '../../contexts/AuthContext';
import UserProfile from '../shared/SharedUserProfile';
import { AuthModal } from '../auth/AuthModal';
import { ProfileModal } from '../shared/ProfileModal';

// ============================================================
// CONSTANTS
// ============================================================

const MAIN_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'actors', label: 'Actors', icon: Users },
  { id: 'items', label: 'Items', icon: Box },
  { id: 'activities', label: 'Activities', icon: Calendar },
    { id: 'planner', label: 'Planner', icon: Target },
  { id: 'systems', label: 'Systems', icon: Sliders }
];

const MOBILE_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'actors', label: 'Actors', icon: Users },
  { id: 'items', label: 'Items', icon: Box },
  { id: 'activities', label: 'Activities', icon: Calendar },
    { id: 'planner', label: 'Planner', icon: Target },
  { id: 'more', label: 'More', icon: MoreHorizontal }
];

const ACTOR_TYPES = [
  { id: 'learner', label: 'Learners', icon: GraduationCap, color: 'blue' },
  { id: 'staff', label: 'Staff', icon: Briefcase, color: 'indigo' },
  { id: 'sgb', label: 'SGB', icon: UsersRound, color: 'amber' },
  { id: 'stakeholder', label: 'Stakeholders', icon: Users, color: 'purple' }
];

const ITEM_STATUS_OPTIONS = [
  { id: ItemStatus.AVAILABLE, label: 'Available', color: 'green' },
  { id: ItemStatus.IN_USE, label: 'In Use', color: 'blue' },
  { id: ItemStatus.MAINTENANCE, label: 'Maintenance', color: 'amber' },
  { id: ItemStatus.MISSING, label: 'Missing', color: 'red' },
  { id: ItemStatus.RETIRED, label: 'Retired', color: 'purple' }
];

const ACTIVITY_TYPE_OPTIONS = ActivityTypeOrder.map((id) => ({
  id,
  label: ActivityTypeMeta[id]?.label || id,
  color: ActivityTypeMeta[id]?.color || 'blue'
}));

const ACTIVITY_COLOR_CLASSES = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  purple: 'bg-purple-500',
  indigo: 'bg-indigo-500',
  slate: 'bg-slate-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  orange: 'bg-orange-500',
  violet: 'bg-violet-500',
  gray: 'bg-gray-500'
};

const LOG_CATEGORY_SUGGESTIONS = [
  'One-to-one',
  'Check-in',
  'Check-out',
  'Behavior',
  'Progress',
  'Communication',
  'Meeting',
  'Incident',
  'Achievement',
  'Support',
  'Maintenance',
  'Follow-up'
];

const LOG_STATUS_OPTIONS = [
  { id: 'open', label: 'Open', color: 'blue' },
  { id: 'in-progress', label: 'In Progress', color: 'amber' },
  { id: 'resolved', label: 'Resolved', color: 'green' },
  { id: 'closed', label: 'Closed', color: 'default' }
];

const LOG_VISIBILITY_OPTIONS = [
  { id: 'internal', label: 'Internal' },
  { id: 'private', label: 'Private' },
  { id: 'public', label: 'Public' }
];

const LOG_TYPE_OPTIONS = [
  { id: LogType.NOTE, label: 'Note' },
  { id: LogType.STATUS, label: 'Status' },
  { id: LogType.ATTENDANCE, label: 'Attendance' },
  { id: LogType.COVERAGE, label: 'Coverage' },
  { id: LogType.SCORE, label: 'Score' },
  { id: LogType.INCIDENT, label: 'Incident' },
  { id: LogType.MILESTONE, label: 'Milestone' }
];

// ============================================================
// UTILITIES
// ============================================================

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffMins = Math.floor((new Date() - date) / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return `${Math.floor(diffMins / 1440)} days ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const STAFF_ROLES = {
  admin: 'Admin',
  educator: 'Educator',
  support: 'Support'
};

const SGB_ROLES = {
  chair: 'Chair',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
  member: 'Member'
};

const STAKEHOLDER_ROLES = {
  parent: 'Parent',
  guardian: 'Guardian',
  sponsor: 'Sponsor',
  community: 'Community',
  other: 'Other'
};

const normalizeActorType = (type) => {
  if (type === 'student') return 'learner';
  if (type === 'teacher') return 'staff';
  return type || 'staff';
};

const getActorSubtitle = (actor) => {
  const type = normalizeActorType(actor.type);
  if (type === 'learner') return actor.className || actor.grade || 'Learner';
  if (type === 'staff') return STAFF_ROLES[actor.staffRole] || actor.position || 'Staff';
  if (type === 'sgb') return SGB_ROLES[actor.sgbRole] || 'SGB';
  if (type === 'stakeholder') return STAKEHOLDER_ROLES[actor.stakeholderRole] || 'Stakeholder';
  return actor.position || '';
};

const getItemStatusMeta = (status) => {
  return ITEM_STATUS_OPTIONS.find(option => option.id === status) || ITEM_STATUS_OPTIONS[0];
};

// ============================================================
// SHARED COMPONENTS
// ============================================================

const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    teal: 'bg-teal-100 text-teal-700',
    orange: 'bg-orange-100 text-orange-700',
    violet: 'bg-violet-100 text-violet-700',
    gray: 'bg-gray-100 text-gray-700'
  };
  const sizes = { xs: 'px-1.5 py-0.5 text-[10px]', sm: 'px-2 py-0.5 text-xs' };
  return <span className={`rounded-full font-medium ${variants[variant]} ${sizes[size]}`}>{children}</span>;
};

const Avatar = ({ src, name, size = 'md' }) => {
  const sizes = { sm: 'h-8 w-8 text-xs', md: 'h-10 w-10 text-sm', lg: 'h-12 w-12 text-base', xl: 'h-16 w-16 text-lg' };
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  if (src) return <img src={src} alt={name} className={`${sizes[size]} rounded-full object-cover`} />;
  return (
    <div className={`${sizes[size]} rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold`}>
      {initials}
    </div>
);
};

const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => (
  <div className="flex items-center gap-3 py-2">
    <Icon className={`h-5 w-5 text-${color}-600`} />
    <span className="text-gray-600">{label}</span>
    <span className="ml-auto font-semibold text-gray-900">{value}</span>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-12 px-4">
    <div className="mx-auto h-14 w-14 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
      <Icon className="h-7 w-7 text-gray-400" />
    </div>
    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500">{description}</p>
  </div>
);

// ============================================================
// LOG COMPOSER
// ============================================================

const LogComposer = ({
  bookId,
  entityType,
  entityId,
  entityName,
  availableCategories = [],
  referenceSources = {},
  onCategoryAdded,
  onLogCreated,
  placeholder = "Add a log entry..."
}) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [logType, setLogType] = useState(LogType.NOTE);
  const [category, setCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [status, setStatus] = useState('');
  const [action, setAction] = useState('');
  const [actionDue, setActionDue] = useState('');
  const [eventAt, setEventAt] = useState('');
  const [referenceType, setReferenceType] = useState(() => {
    if (entityType === 'activity' || entityType === 'lesson' || entityType === 'assessment') return 'activity';
    return entityType || 'actor';
  });
  const [referenceId, setReferenceId] = useState('');
  const [references, setReferences] = useState([]);
  const [location, setLocation] = useState('');
  const [visibility, setVisibility] = useState('internal');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const categoryOptions = useMemo(() => {
    const combined = [...LOG_CATEGORY_SUGGESTIONS, ...(availableCategories || []), category]
      .map(option => option?.trim())
      .filter(Boolean);
    return Array.from(new Set(combined));
  }, [availableCategories, category]);

  const referenceOptions = useMemo(() => {
    const listMap = {
      actor: referenceSources?.actors || [],
      item: referenceSources?.items || [],
      activity: referenceSources?.activities || []
    };
    return listMap[referenceType] || [];
  }, [referenceSources, referenceType]);

  const handleAddReference = () => {
    if (!referenceId) return;
    const reference = referenceOptions.find(option => option.id === referenceId);
    if (!reference) return;
    const exists = references.some(item => item.type === referenceType && item.id === referenceId);
    if (exists) return;
    setReferences(prev => [...prev, { type: referenceType, id: reference.id, name: reference.name || reference.title || reference.label }]);
    setReferenceId('');
  };

  const handleAddCategory = async (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setCategory(trimmed);
    setIsAddingCategory(false);
    setNewCategory('');
    if (!entityType || !entityId) return;
    const exists = (availableCategories || []).some(cat => cat.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      try {
        await schoolmateService.addEntityLogCategory(bookId, entityType, entityId, trimmed);
        onCategoryAdded?.(trimmed);
      } catch (err) { console.error(err); }
    }
  };

  const handleSubmit = async () => {
    const canSubmit = note.trim() || category || status || action.trim() || eventAt || location.trim() || references.length > 0;
    if (!canSubmit) return;
    setSaving(true);
    try {
      const trimmedCategory = category.trim();
      if (trimmedCategory && entityType && entityId) {
        const exists = (availableCategories || []).some(cat => cat.toLowerCase() === trimmedCategory.toLowerCase());
        if (!exists) {
          await schoolmateService.addEntityLogCategory(bookId, entityType, entityId, trimmedCategory);
          onCategoryAdded?.(trimmedCategory);
        }
      }

      await schoolmateService.createLog(bookId, {
        type: logType,
        entityType: entityType || 'school',
        entityId: entityId || bookId,
        entityName,
        note: note.trim() || '',
        category: trimmedCategory || null,
        status: status || null,
        action: action.trim() || null,
        actionDue: actionDue ? new Date(actionDue).toISOString() : null,
        eventAt: eventAt ? new Date(eventAt).toISOString() : null,
        references: references.length > 0 ? references : null,
        location: location.trim() || null,
        visibility
      });
      setNote('');
      setAction('');
      setActionDue('');
      setEventAt('');
      setReferences([]);
      setReferenceId('');
      setLocation('');
      onLogCreated?.();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`p-2.5 rounded-lg border ${showAdvanced ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
          aria-label={showAdvanced ? 'Hide log details' : 'Show log details'}
        >
          <Sliders className="h-4 w-4" />
        </button>
        {showAdvanced ? (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder={placeholder}
            className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
        <button className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <Paperclip className="h-5 w-5" />
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
        >
          {saving ? 'Saving...' : 'Log'}
        </button>
      </div>

      {showAdvanced && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Log type</label>
              <select
                value={logType}
                onChange={(e) => setLogType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LOG_TYPE_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Category</label>
              {!isAddingCategory ? (
                <select
                  value={category}
                  onChange={(e) => {
                    if (e.target.value === '__add__') {
                      setIsAddingCategory(true);
                      setCategory('');
                    } else {
                      setCategory(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select category</option>
                  {categoryOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                  <option value="__add__">+ Add new category</option>
                </select>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="New category"
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddCategory(newCategory)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No status</option>
                {LOG_STATUS_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Visibility</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LOG_VISIBILITY_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Action (optional)</label>
              <input
                value={action}
                onChange={(e) => setAction(e.target.value)}
                placeholder="Follow-up, call guardian, schedule session"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Action due</label>
              <input
                type="datetime-local"
                value={actionDue}
                onChange={(e) => setActionDue(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Event date/time</label>
              <input
                type="datetime-local"
                value={eventAt}
                onChange={(e) => setEventAt(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Office, Classroom 2B"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Reference type</label>
              <select
                value={referenceType}
                onChange={(e) => setReferenceType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="actor">Actors</option>
                <option value="item">Items</option>
                <option value="activity">Activities</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Reference</label>
              <div className="flex items-center gap-2">
                <select
                  value={referenceId}
                  onChange={(e) => setReferenceId(e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select reference</option>
                  {referenceOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name || option.title || option.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddReference}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium"
                >
                  Add
                </button>
              </div>
            </div>
            {references.length > 0 && (
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">References</label>
                <div className="flex flex-wrap gap-2">
                  {references.map(ref => (
                    <span key={`${ref.type}-${ref.id}`} className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {ref.name || ref.id}
                      <button
                        type="button"
                        onClick={() => setReferences(prev => prev.filter(item => !(item.type === ref.type && item.id === ref.id)))}
                        className="text-blue-700 hover:text-blue-900"
                        aria-label={`Remove ${ref.name || ref.id}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// LOG FEED ITEM
// ============================================================

const LogFeedItem = ({ log, onActorClick }) => {
  const statusMeta = LOG_STATUS_OPTIONS.find(option => option.id === log.status);
  const visibilityVariant = log.visibility === 'public' ? 'green' : log.visibility === 'private' ? 'amber' : 'blue';
  const references = Array.isArray(log.references) ? log.references : [];

  return (
    <div className="flex gap-3 p-3 hover:bg-gray-50 rounded-lg">
      <Avatar name={log.createdByName || log.actorName || 'User'} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm">
            <button onClick={() => onActorClick?.(log.actorId)} className="font-semibold text-gray-900 hover:text-blue-600">
              {log.actorName || log.createdByName || 'Unknown'}
            </button>
            {log.type === LogType.SCORE ? (
              <span className="text-gray-600"> scored <Badge variant={log.percent >= 50 ? 'green' : 'red'}>{log.percent}%</Badge> on {log.entityName}</span>
            ) : (
              <span className="text-gray-600"> {log.note || 'Update recorded.'}</span>
            )}
            {log.action && <span className="text-gray-500"> · Action: {log.action}</span>}
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{formatRelativeTime(log.createdAt)}</span>
        </div>
        {(log.category || log.status || log.visibility || log.eventAt || log.actionDue || log.location || references.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
            {log.category && <Badge variant="purple">{log.category}</Badge>}
            {statusMeta && <Badge variant={statusMeta.color}>{statusMeta.label}</Badge>}
            {log.visibility && <Badge variant={visibilityVariant}>{log.visibility}</Badge>}
            {log.eventAt && <span>When: {formatDateTime(log.eventAt)}</span>}
            {log.actionDue && <span>Action due: {formatDateTime(log.actionDue)}</span>}
            {log.location && <span>Location: {log.location}</span>}
            {references.length > 0 && (
              <span>
                References: {references.map(ref => ref.name || ref.id).join(', ')}
              </span>
            )}
          </div>
        )}
        {log.createdByName && log.createdByName !== log.actorName && (
          <p className="text-xs text-gray-400 mt-0.5">by {log.createdByName}</p>
        )}
      </div>
    </div>
  );
};

// ============================================================
// ACTOR DETAIL PANEL
// ============================================================

const ActorDetailPanel = ({ actor, bookId, onClose, onRefresh, onCategoryAdded, actors = [], items = [], activities = [], classes = [], scheduleSlots = [] }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const [showTimetable, setShowTimetable] = useState(false);
  const [timetableMode, setTimetableMode] = useState('school');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  useEffect(() => { if (actor) loadLogs(); }, [actor]);

  const loadLogs = async () => {
    try {
      const logsData = await schoolmateService.getLogs(bookId, { actorId: actor.id, limit: 50 });
      const scopedLogs = logsData.filter(log => log.entityId === actor.id);
      setLogs(scopedLogs);
    } catch (err) { console.error(err); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'logs', label: 'Logs', badge: logs.length },
    { id: 'attendance', label: 'Attendance' },
    { id: 'marks', label: 'Marks' }
  ];

  if (!actor) return null;

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const dayLabels = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri' };
  const uniquePeriods = Array.from(new Set(scheduleSlots.map(slot => slot.period))).sort((a, b) => a - b);
  const subjectOptions = Array.from(new Set(scheduleSlots.map(slot => slot.subjectName).filter(Boolean)));
  const teacherOptions = actors.filter(a => normalizeActorType(a.type) === 'staff' && (!a.staffRole || a.staffRole === 'educator'));
  const classOptions = classes.length > 0
    ? classes
    : Array.from(new Set(scheduleSlots.map(slot => ({ id: slot.classId, name: slot.className })))).filter(c => c.id || c.name);

  const filteredSlots = scheduleSlots.filter(slot => {
    if (timetableMode === 'grade' && selectedClassId) return slot.classId === selectedClassId || slot.className === selectedClassId;
    if (timetableMode === 'subject' && selectedSubject) return slot.subjectName === selectedSubject;
    if (timetableMode === 'teacher' && selectedTeacherId) return slot.teacherId === selectedTeacherId;
    return true;
  });

  const getSlotLabel = (slot) => {
    const primary = slot.subjectName || 'Subject';
    const secondary = timetableMode === 'teacher'
      ? (slot.className || 'Class')
      : timetableMode === 'subject'
        ? (slot.className || 'Class')
        : (slot.teacherName || 'Teacher');
    return { primary, secondary };
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start mb-4">
          <div className="flex items-center gap-3">
            <Avatar name={actor.name} size="xl" src={actor.photoUrl} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">{actor.name}</h2>
              <p className="text-sm text-gray-500">{getActorSubtitle(actor)}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowTimetable((prev) => !prev)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${showTimetable ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:bg-gray-100'}`}
            >
              {showTimetable ? 'Hide Timetable' : 'Show Timetable'}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {(actor.guardianName || actor.phone || actor.email || actor.address || actor.parentNames?.length) && (
          <div className="flex flex-wrap gap-3 text-sm">
            {actor.guardianName && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <User className="h-3.5 w-3.5 text-gray-500" />
                {actor.guardianName}, {actor.guardianRelation || 'Guardian'}
              </span>
            )}
            {actor.parentNames?.length > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <Users className="h-3.5 w-3.5 text-gray-500" />
                {actor.parentNames.join(', ')}
              </span>
            )}
            {actor.phone && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <Phone className="h-3.5 w-3.5 text-gray-500" />{actor.phone}
              </span>
            )}
            {actor.altPhone && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <Phone className="h-3.5 w-3.5 text-gray-500" />{actor.altPhone}
              </span>
            )}
            {actor.email && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <Mail className="h-3.5 w-3.5 text-gray-500" />{actor.email}
              </span>
            )}
            {actor.address && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
                <MapPin className="h-3.5 w-3.5 text-gray-500" />{actor.address}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.badge > 0 && <Badge variant="blue" size="xs">{tab.badge}</Badge>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden flex">
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 mb-1">Class</p>
                  <p className="font-semibold text-blue-900">{actor.className || 'Grade 10A'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">ID</p>
                  <p className="font-semibold text-gray-900">{actor.studentId || '20231745'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Age</p>
                  <p className="font-semibold text-gray-900">{actor.age || '16'} years old</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Gender</p>
                  <p className="font-semibold text-gray-900">{actor.gender || 'Male'}</p>
                </div>
              </div>

              {actor.subjectNames?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Subjects</h4>
                  <div className="flex flex-wrap gap-2">
                    {actor.subjectNames.map(subject => (
                      <span key={subject} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Recent Logs</h4>
                {logs.length === 0 ? (
                  <p className="text-sm text-gray-500">No logs yet</p>
                ) : (
                  <div className="space-y-1">
                    {logs.slice(0, 5).map(log => <LogFeedItem key={log.id} log={log} />)}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="divide-y divide-gray-100">
              {logs.length === 0 ? (
                <EmptyState icon={FileText} title="No logs" description="Activity will appear here" />
              ) : (
                logs.map(log => <LogFeedItem key={log.id} log={log} />)
              )}
            </div>
          )}

          {activeTab === 'attendance' && <EmptyState icon={Users} title="Attendance" description="Records will appear here" />}
          {activeTab === 'marks' && <EmptyState icon={Award} title="Marks" description="Assessment marks will appear here" />}
        </div>

        {showTimetable && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowTimetable(false)}
            />
            <aside className="relative h-full w-full max-w-md bg-white shadow-xl flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Timetable</h3>
                  <button onClick={() => setShowTimetable(false)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  <select
                    value={timetableMode}
                    onChange={(e) => setTimetableMode(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                  >
                    <option value="school">School Timetable</option>
                    <option value="grade">Grade Timetable</option>
                    <option value="subject">Subject Timetable</option>
                    <option value="teacher">Teacher Timetable</option>
                  </select>
                  {timetableMode === 'grade' && (
                    <select
                      value={selectedClassId}
                      onChange={(e) => setSelectedClassId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                    >
                      <option value="">Select grade/class</option>
                      {classOptions.map(cls => (
                        <option key={cls.id || cls.name} value={cls.id || cls.name}>{cls.name || cls.id}</option>
                      ))}
                    </select>
                  )}
                  {timetableMode === 'subject' && (
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                    >
                      <option value="">Select subject</option>
                      {subjectOptions.map(subject => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  )}
                  {timetableMode === 'teacher' && (
                    <select
                      value={selectedTeacherId}
                      onChange={(e) => setSelectedTeacherId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                    >
                      <option value="">Select teacher</option>
                      {teacherOptions.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {filteredSlots.length === 0 ? (
                  <EmptyState icon={Grid3X3} title="No timetable slots" description="Add schedule slots in School Manager." />
                ) : (
                  <div className="space-y-3">
                    {dayOrder.map(day => {
                      const daySlots = filteredSlots.filter(slot => slot.day === day);
                      if (daySlots.length === 0) return null;
                      return (
                        <div key={day}>
                          <div className="text-xs font-semibold text-gray-500 mb-2">{dayLabels[day]}</div>
                          <div className="space-y-2">
                            {uniquePeriods.map(period => {
                              const slot = daySlots.find(s => s.period === period);
                              if (!slot) return null;
                              const label = getSlotLabel(slot);
                              return (
                                <div key={`${day}-${period}`} className="border rounded-lg p-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">P{period} • {slot.startTime || ''}</span>
                                    {slot.className && timetableMode === 'school' && (
                                      <Badge variant="slate">{slot.className}</Badge>
                                    )}
                                  </div>
                                  <div className="text-sm font-medium text-gray-900">{label.primary}</div>
                                  <div className="text-xs text-gray-500">{label.secondary}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* Log Composer */}
      <div className="sticky bottom-0 bg-white">
        <LogComposer
          bookId={bookId}
          entityType="actor"
          entityId={actor.id}
          entityName={actor.name}
          availableCategories={actor.logCategories || []}
          referenceSources={{ actors, items, activities }}
          onCategoryAdded={(category) => onCategoryAdded?.(actor.id, category)}
          onLogCreated={loadLogs}
          placeholder={`Add a log about ${actor.name}...`}
        />
      </div>
    </div>
  );
};

// ============================================================
// ITEM DETAIL PANEL
// ============================================================

const ItemDetailPanel = ({ item, bookId, actors = [], items = [], activities = [], onClose, onRefresh, onEdit, onCategoryAdded }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const statusMeta = getItemStatusMeta(item?.status);

  useEffect(() => { if (item) loadLogs(); }, [item]);

  const loadLogs = async () => {
    try {
      const logsData = await schoolmateService.getLogs(bookId, { entityType: 'item', entityId: item.id, limit: 50 });
      const scopedLogs = logsData.filter(log => log.entityId === item.id);
      setLogs(scopedLogs);
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (newStatus) => {
    if (!item || newStatus === item.status) return;
    try {
      await schoolmateService.updateItemStatus(bookId, item.id, newStatus);
      onRefresh?.();
    } catch (err) { console.error(err); }
  };

  if (!item) return null;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{item.name}</h2>
            <p className="text-sm text-gray-500">{item.category || 'General item'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={statusMeta.color}>{statusMeta.label}</Badge>
            <button onClick={onEdit} className="px-3 py-1.5 text-sm text-blue-600 font-medium">Edit</button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-sm">
          <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
            <MapPin className="h-3.5 w-3.5 text-gray-500" />
            {item.location || 'Location unset'}
          </span>
          {item.assignedToName && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <User className="h-3.5 w-3.5 text-gray-500" />
              {item.assignedToName}
            </span>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200">
        {['overview', 'logs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === tab ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab === 'overview' ? 'Overview' : 'Logs'}
            {tab === 'logs' && logs.length > 0 && <Badge variant="blue" size="xs">{logs.length}</Badge>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Status</p>
                <select
                  value={item.status || ItemStatus.AVAILABLE}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-sm"
                >
                  {ITEM_STATUS_OPTIONS.map(option => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Quantity</p>
                <p className="font-semibold text-gray-900">{item.quantity || 1}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Serial</p>
                <p className="font-semibold text-gray-900">{item.serialNumber || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Condition</p>
                <p className="font-semibold text-gray-900">{item.condition || 'Good'}</p>
              </div>
            </div>

            {item.description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            )}

            {item.tags?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="divide-y divide-gray-100">
            {logs.length === 0 ? (
              <EmptyState icon={FileText} title="No logs" description="Item updates will appear here" />
            ) : (
              logs.map(log => <LogFeedItem key={log.id} log={log} onActorClick={(actorId) => {
                const actor = actors.find(a => a.id === actorId);
                if (actor) {
                  // noop placeholder for future deep linking
                }
              }} />)
            )}
          </div>
        )}
      </div>

      <LogComposer
        bookId={bookId}
        entityType="item"
        entityId={item.id}
        entityName={item.name}
        availableCategories={item.logCategories || []}
        referenceSources={{ actors, items, activities }}
        onCategoryAdded={(category) => onCategoryAdded?.(item.id, category)}
        onLogCreated={loadLogs}
        placeholder={`Add a log about ${item.name}...`}
      />
    </div>
  );
};

// ============================================================
// ACTIVITY DETAIL PANEL
// ============================================================

const ActivityDetailPanel = ({ activity, bookId, actors = [], items = [], activities = [], onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);

  useEffect(() => { if (activity) loadLogs(); }, [activity]);

  const loadLogs = async () => {
    try {
      const logsData = await schoolmateService.getLogs(bookId, { entityType: 'activity', entityId: activity.id, limit: 50 });
      const scopedLogs = logsData.filter(log => log.entityId === activity.id);
      setLogs(scopedLogs);
    } catch (err) { console.error(err); }
  };

  if (!activity) return null;

  const typeMeta = ActivityTypeMeta[activity.activityType] || { label: activity.activityType || 'Activity', color: 'slate' };
  const statusMeta = ActivityStatusMeta[activity.status] || { label: activity.status || 'Pending', color: 'slate' };
  const references = Array.isArray(activity.references) ? activity.references : [];

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start gap-4 mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{activity.name}</h2>
            <p className="text-sm text-gray-500">{activity.description || 'Activity details'}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant={typeMeta.color}>{typeMeta.label}</Badge>
            <Badge variant={statusMeta.color}>{statusMeta.label}</Badge>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-sm">
          {activity.dueDate && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              Due {formatDate(activity.dueDate)}
            </span>
          )}
          {activity.startTime && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {formatDateTime(activity.startTime)}
            </span>
          )}
          {activity.durationMins && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Clock className="h-3.5 w-3.5 text-gray-500" />
              {activity.durationMins} mins
            </span>
          )}
          {activity.mode && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Sliders className="h-3.5 w-3.5 text-gray-500" />
              {activity.mode}
            </span>
          )}
          {activity.location && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <MapPin className="h-3.5 w-3.5 text-gray-500" />
              {activity.location}
            </span>
          )}
          {activity.priority && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Award className="h-3.5 w-3.5 text-gray-500" />
              {activity.priority}
            </span>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200">
        {['overview', 'logs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === tab ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab === 'overview' ? 'Overview' : 'Logs'}
            {tab === 'logs' && logs.length > 0 && <Badge variant="blue" size="xs">{logs.length}</Badge>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-4 space-y-4">
            {activity.meetingLink && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Meeting Link</h4>
                <a href={activity.meetingLink} className="text-sm text-blue-600 hover:underline" target="_blank" rel="noreferrer">
                  {activity.meetingLink}
                </a>
              </div>
            )}

            {activity.agenda && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Agenda</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{activity.agenda}</p>
              </div>
            )}

            {activity.entry && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Entry</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{activity.entry}</p>
              </div>
            )}

            {activity.rationale && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Rationale</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{activity.rationale}</p>
              </div>
            )}

            {references.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">References</h4>
                <div className="flex flex-wrap gap-2">
                  {references.map(ref => (
                    <span key={`${ref.type}-${ref.id}`} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {ref.name || ref.id} • {ref.type}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {logs.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Recent Logs</h4>
                <div className="space-y-1">
                  {logs.slice(0, 5).map(log => <LogFeedItem key={log.id} log={log} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="divide-y divide-gray-100">
            {logs.length === 0 ? (
              <EmptyState icon={FileText} title="No logs" description="Activity updates will appear here" />
            ) : (
              logs.map(log => <LogFeedItem key={log.id} log={log} />)
            )}
          </div>
        )}
      </div>

      <LogComposer
        bookId={bookId}
        entityType="activity"
        entityId={activity.id}
        entityName={activity.name}
        referenceSources={{ actors, items, activities }}
        onLogCreated={loadLogs}
        placeholder={`Add a log about ${activity.name}...`}
      />
    </div>
  );
};

// ============================================================
// ITEMS LIST
// ============================================================

const ItemsSidebar = ({ items, selectedCategory, onSelectCategory }) => {
  const counts = useMemo(() => {
    return items.reduce((acc, item) => {
      const category = item.category || 'Inventory';
      acc[category] = (acc[category] || 0) + 1;
      acc.all += 1;
      return acc;
    }, { all: 0 });
  }, [items]);

  const baseCategories = [
    'Timetable',
    'Plans',
    'Tasks',
    'Resources',
    'Periods',
    'Subjects',
    'Assessments',
    'Marklists',
    'Inventory'
  ];
  const dynamicCategories = Array.from(new Set(items.map(item => item.category).filter(Boolean)));
  const categories = ['all', ...Array.from(new Set([...baseCategories, ...dynamicCategories]))];

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Items</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {categories.map(category => {
          const isActive = selectedCategory === category || (!selectedCategory && category === 'all');
          const count = category === 'all' ? counts.all : (counts[category] || 0);
          return (
            <button
              key={category}
              onClick={() => onSelectCategory?.(category)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <Box className="h-4 w-4" />
              </span>
              <span className="flex-1 font-medium">{category === 'all' ? 'All' : category}</span>
              <span className="text-sm text-gray-400">{count}</span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-gray-200 p-4 text-xs text-gray-500 space-y-2">
        <div className="font-semibold text-gray-700">Items</div>
        <div>Filter resources by category or type.</div>
      </div>
    </div>
  );
};

const ItemsListView = ({
  items,
  selectedItem,
  onSelectItem,
  categoryFilter: externalCategoryFilter,
  onCategoryFilterChange,
  onCreateItem
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localCategoryFilter, setLocalCategoryFilter] = useState('all');
  const categoryFilter = externalCategoryFilter ?? localCategoryFilter;
  const setCategoryFilter = onCategoryFilterChange ?? setLocalCategoryFilter;

  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter !== 'all') result = result.filter(i => (i.category || 'Inventory') === categoryFilter);
    if (statusFilter !== 'all') result = result.filter(i => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.category?.toLowerCase().includes(q) ||
        i.location?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [items, search, statusFilter, categoryFilter]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">Items</h2>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            All
          </button>
          {ITEM_STATUS_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setStatusFilter(option.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                statusFilter === option.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredItems.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No items found</p>
        ) : (
          <div className="space-y-1">
            {filteredItems.map(item => {
              const statusMeta = getItemStatusMeta(item.status);
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    selectedItem?.id === item.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Box className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500 truncate">{item.category || 'General item'} • {item.location || 'No location'}</p>
                  </div>
                  <Badge variant={statusMeta.color} size="xs">{statusMeta.label}</Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// PLANNER
// ============================================================

const PLAN_TYPE_META = [
  { id: 'improvement', label: 'Improvement Plan', icon: Target, color: 'blue' },
  { id: 'operational', label: 'Operational Plan', icon: ClipboardList, color: 'indigo' },
  { id: 'teaching', label: 'Teaching Plan', icon: GraduationCap, color: 'violet' },
  { id: 'assessment', label: 'Assessment Program', icon: Award, color: 'amber' },
  { id: 'departmental', label: 'Departmental Plan', icon: Users, color: 'teal' },
  { id: 'subject', label: 'Subject Plan', icon: BookOpen, color: 'blue' },
  { id: 'lesson', label: 'Lesson Plan', icon: FileText, color: 'emerald' },
  { id: 'other', label: 'Other Plan', icon: FileText, color: 'slate' }
];

const formatPlanDate = (value) => {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
};

const normalizePlanType = (plan) => {
  const raw = `${plan?.planType || plan?.type || plan?.category || plan?.planCategory || ''}`.toLowerCase();
  if (raw.includes('subject') && raw.includes('improvement')) return 'subject';
  if (raw.includes('improvement') || raw.includes('sip')) return 'improvement';
  if (raw.includes('operational')) return 'operational';
  if (raw.includes('annual') || raw.includes('teaching') || raw.includes('atp')) return 'teaching';
  if (raw.includes('assessment') || raw.includes('poa')) return 'assessment';
  if (raw.includes('department')) return 'departmental';
  if (raw.includes('lesson')) return 'lesson';
  return 'other';
};

const PlannerSidebar = ({ plans, selectedType, onSelectType }) => {
  const counts = useMemo(() => {
    return plans.reduce((acc, plan) => {
      const typeId = normalizePlanType(plan);
      acc[typeId] = (acc[typeId] || 0) + 1;
      acc.all += 1;
      return acc;
    }, { all: 0 });
  }, [plans]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Planner</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <button
          onClick={() => onSelectType?.('all')}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
            selectedType === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
            selectedType === 'all' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
          }`}>
            <ClipboardList className="h-4 w-4" />
          </span>
          <span className="flex-1 font-medium">Planner</span>
          <span className="text-sm text-gray-400">{counts.all}</span>
        </button>
        {PLAN_TYPE_META.map(type => {
          const Icon = type.icon;
          const isActive = selectedType === type.id;
          return (
            <button
              key={type.id}
              onClick={() => onSelectType?.(type.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex-1 font-medium">{type.label}</span>
              <span className="text-sm text-gray-400">{counts[type.id] || 0}</span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-gray-200 p-4 text-xs text-gray-500 space-y-2">
        <div className="font-semibold text-gray-700">Plans</div>
        <div>Plan items move to activities when actioned.</div>
      </div>
    </div>
  );
};

const PlannerListView = ({
  plans,
  selectedPlan,
  onSelectPlan,
  typeFilter: externalTypeFilter,
  onTypeFilterChange,
  onCreatePlan
}) => {
  const [search, setSearch] = useState('');
  const [localTypeFilter, setLocalTypeFilter] = useState('all');
  const typeFilter = externalTypeFilter ?? localTypeFilter;
  const setTypeFilter = onTypeFilterChange ?? setLocalTypeFilter;

  const filteredPlans = useMemo(() => {
    let result = plans;
    if (typeFilter !== 'all') {
      result = result.filter(plan => normalizePlanType(plan) === typeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(plan =>
        plan.name?.toLowerCase().includes(q) ||
        plan.title?.toLowerCase().includes(q) ||
        plan.type?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [plans, search, typeFilter]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search plans..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg bg-white">
            <option value="all">All</option>
            {PLAN_TYPE_META.map(type => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>
          <button
            onClick={onCreatePlan}
            disabled={!onCreatePlan}
            className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
              onCreatePlan ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Plus className="h-4 w-4" />
            Add Plan
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredPlans.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No plans found</p>
        ) : (
          <div className="space-y-1">
            {filteredPlans.map(plan => {
              const typeId = normalizePlanType(plan);
              const typeMeta = PLAN_TYPE_META.find(t => t.id === typeId) || PLAN_TYPE_META[0];
              const Icon = typeMeta.icon;
              const title = plan.name || plan.title || 'Untitled plan';
              const start = plan.startDate || plan.start || plan.startAt;
              const end = plan.endDate || plan.end || plan.endAt;
              return (
                <button
                  key={plan.id}
                  onClick={() => onSelectPlan(plan)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    selectedPlan?.id === plan.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center bg-${typeMeta.color}-100 text-${typeMeta.color}-600`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{title}</p>
                    <p className="text-xs text-gray-500 truncate">{typeMeta.label}</p>
                  </div>
                  <div className="text-xs text-gray-400 text-right">
                    <div>{formatPlanDate(start) || 'No start'}</div>
                    <div>{formatPlanDate(end) || 'No end'}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const PlannerDetailPanel = ({ plan, bookId, actors = [], items = [], activities = [], onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => { if (plan) loadLogs(); }, [plan]);
  useEffect(() => { if (plan) loadActions(); }, [plan]);

  const loadLogs = async () => {
    try {
      const logsData = await schoolmateService.getLogs(bookId, { entityType: 'plan', entityId: plan.id, limit: 50 });
      const scopedLogs = logsData.filter(log => log.entityId === plan.id);
      setLogs(scopedLogs);
    } catch (err) { console.error(err); }
  };

  const loadActions = async () => {
    if (!plan?.id) {
      setActions([]);
      return;
    }
    try {
      const actionsData = await schoolmateService.getPlannerActions(bookId, plan.id);
      setActions(actionsData);
    } catch (err) { console.error(err); }
  };

  if (!plan) return null;

  const typeId = normalizePlanType(plan);
  const typeMeta = PLAN_TYPE_META.find(t => t.id === typeId) || PLAN_TYPE_META[0];
  const TypeIcon = typeMeta.icon;
  const title = plan.name || plan.title || 'Untitled plan';
  const owner = plan.ownerName || plan.leadName || plan.owner || '';
  const start = plan.startDate || plan.start || plan.startAt;
  const end = plan.endDate || plan.end || plan.endAt;
  const getActionStatusMeta = (action) => {
    const hasGraduated = Boolean(action?.activityId);
    const now = new Date();
    const startDate = action?.startDate ? new Date(action.startDate) : null;
    if (hasGraduated) return { label: 'Graduated', color: 'purple' };
    if (startDate && startDate <= now) return { label: 'Ready', color: 'amber' };
    return { label: action?.status ? `${action.status}` : 'Planned', color: 'blue' };
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start gap-4 mb-3">
          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center bg-${typeMeta.color}-100 text-${typeMeta.color}-600`}>
            <TypeIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">{title}</h2>
            <p className="text-sm text-gray-500 truncate">{typeMeta.label}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant={typeMeta.color}>{typeMeta.label}</Badge>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-sm">
          {start && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {formatPlanDate(start)}
            </span>
          )}
          {end && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {formatPlanDate(end)}
            </span>
          )}
          {owner && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <User className="h-3.5 w-3.5 text-gray-500" />
              {owner}
            </span>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200">
        {['overview', 'logs', 'actions', 'resources'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === tab ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab === 'overview' ? 'Overview' : tab === 'logs' ? 'Logs' : tab === 'actions' ? 'Actions' : 'Resources'}
            {tab === 'logs' && logs.length > 0 && <Badge variant="blue" size="xs">{logs.length}</Badge>}
            {tab === 'actions' && actions.length > 0 && <Badge variant="blue" size="xs">{actions.length}</Badge>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-4 space-y-4">
            {plan.description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Overview</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{plan.description}</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              Actions live here and move to Activities once actioned.
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Actions</h4>
                {actions.length > 0 && <Badge variant="blue" size="xs">{actions.length}</Badge>}
              </div>
              {actions.length === 0 ? (
                <p className="text-sm text-gray-500">No actions recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {actions.slice(0, 3).map(action => {
                    const statusMeta = getActionStatusMeta(action);
                    return (
                      <div key={action.id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-white p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{action.name}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
                            {(action.startDate || action.endDate) && (
                              <span>
                                {action.startDate ? formatPlanDate(action.startDate) : 'No start'}
                                {action.endDate ? ` • ${formatPlanDate(action.endDate)}` : ''}
                              </span>
                            )}
                            {statusMeta && <Badge variant={statusMeta.color}>{statusMeta.label}</Badge>}
                            {action.assigneeName && <Badge variant="slate">{action.assigneeName}</Badge>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {actions.length > 3 && (
                    <p className="text-xs text-gray-500">See all actions in the Actions tab.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="divide-y divide-gray-100">
            {logs.length === 0 ? (
              <EmptyState icon={FileText} title="No logs" description="Plan updates will appear here" />
            ) : (
              logs.map(log => <LogFeedItem key={log.id} log={log} />)
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="p-4 space-y-3">
            {actions.length === 0 ? (
              <EmptyState icon={ClipboardList} title="No actions yet" description="Add actions to structure this plan." />
            ) : (
              actions.map(action => {
                const statusMeta = getActionStatusMeta(action);
                return (
                  <div key={action.id} className="rounded-xl border border-gray-200 p-3">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                        <ClipboardList className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{action.name}</p>
                        {action.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{action.description}</p>}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-2">
                          {(action.startDate || action.endDate) && (
                            <span>
                              {action.startDate ? formatPlanDate(action.startDate) : 'No start'}
                              {action.endDate ? ` • ${formatPlanDate(action.endDate)}` : ''}
                            </span>
                          )}
                          {statusMeta && <Badge variant={statusMeta.color}>{statusMeta.label}</Badge>}
                          {action.assigneeName && <Badge variant="slate">{action.assigneeName}</Badge>}
                        </div>
                      </div>
                      {action.createdAt && (
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatRelativeTime(action.createdAt)}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="p-4">
            <EmptyState icon={Box} title="No resources linked" description="Attach resources to support this plan." />
          </div>
        )}
      </div>

      <LogComposer
        bookId={bookId}
        entityType="plan"
        entityId={plan.id}
        entityName={title}
        referenceSources={{ actors, items, activities }}
        onLogCreated={loadLogs}
        placeholder={`Add a log about ${title}...`}
      />
    </div>
  );
};

// ============================================================
// SYSTEMS
// ============================================================

const SYSTEM_TYPE_META = [
  { id: 'vision', label: 'Vision', icon: Eye, color: 'blue' },
  { id: 'mission', label: 'Mission', icon: Compass, color: 'indigo' },
  { id: 'values', label: 'Values', icon: Heart, color: 'amber' },
  { id: 'goals', label: 'Goals', icon: Target, color: 'emerald' },
  { id: 'targets', label: 'Targets', icon: Flag, color: 'purple' },
  { id: 'strategy', label: 'Strategy', icon: Sliders, color: 'teal' },
  { id: 'organogram', label: 'Organogram', icon: UsersRound, color: 'violet' },
  { id: 'qms', label: 'QMS', icon: ShieldCheck, color: 'blue' },
  { id: 'cptd', label: 'CPTD', icon: Award, color: 'orange' },
  { id: 'policies', label: 'Policies', icon: ClipboardList, color: 'slate' },
  { id: 'processes', label: 'Processes', icon: Grid3X3, color: 'indigo' },
  { id: 'procedures', label: 'Procedures', icon: FileText, color: 'gray' },
  { id: 'other', label: 'Other', icon: FileText, color: 'slate' }
];

const normalizeSystemType = (system) => {
  const raw = `${system?.systemType || system?.type || system?.category || system?.name || ''}`.toLowerCase();
  if (raw.includes('vision')) return 'vision';
  if (raw.includes('mission')) return 'mission';
  if (raw.includes('value')) return 'values';
  if (raw.includes('goal')) return 'goals';
  if (raw.includes('target')) return 'targets';
  if (raw.includes('strategy')) return 'strategy';
  if (raw.includes('organogram') || raw.includes('organogramme')) return 'organogram';
  if (raw.includes('qms') || raw.includes('quality')) return 'qms';
  if (raw.includes('cptd') || raw.includes('development')) return 'cptd';
  if (raw.includes('polic')) return 'policies';
  if (raw.includes('process')) return 'processes';
  if (raw.includes('procedure')) return 'procedures';
  return 'other';
};

const SystemsSidebar = ({ systems, selectedType, onSelectType }) => {
  const counts = useMemo(() => {
    return systems.reduce((acc, system) => {
      const typeId = normalizeSystemType(system);
      acc[typeId] = (acc[typeId] || 0) + 1;
      acc.all += 1;
      return acc;
    }, { all: 0 });
  }, [systems]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Systems</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <button
          onClick={() => onSelectType?.('all')}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
            selectedType === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
            selectedType === 'all' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
          }`}>
            <Sliders className="h-4 w-4" />
          </span>
          <span className="flex-1 font-medium">All</span>
          <span className="text-sm text-gray-400">{counts.all}</span>
        </button>
        {SYSTEM_TYPE_META.map(type => {
          const Icon = type.icon;
          const isActive = selectedType === type.id;
          return (
            <button
              key={type.id}
              onClick={() => onSelectType?.(type.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex-1 font-medium">{type.label}</span>
              <span className="text-sm text-gray-400">{counts[type.id] || 0}</span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-gray-200 p-4 text-xs text-gray-500 space-y-2">
        <div className="font-semibold text-gray-700">School systems</div>
        <div>Vision, goals, strategy, and supporting systems.</div>
      </div>
    </div>
  );
};

const SystemsListView = ({
  systems,
  selectedSystem,
  onSelectSystem,
  typeFilter: externalTypeFilter,
  onTypeFilterChange,
  onCreateSystem
}) => {
  const [search, setSearch] = useState('');
  const [localTypeFilter, setLocalTypeFilter] = useState('all');
  const typeFilter = externalTypeFilter ?? localTypeFilter;
  const setTypeFilter = onTypeFilterChange ?? setLocalTypeFilter;

  const filteredSystems = useMemo(() => {
    let result = systems;
    if (typeFilter !== 'all') {
      result = result.filter(system => normalizeSystemType(system) === typeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(system =>
        system.name?.toLowerCase().includes(q) ||
        system.title?.toLowerCase().includes(q) ||
        system.type?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [systems, search, typeFilter]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search systems..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg bg-white">
            <option value="all">All types</option>
            {SYSTEM_TYPE_META.map(type => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>
          <button
            onClick={onCreateSystem}
            disabled={!onCreateSystem}
            className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
              onCreateSystem ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Plus className="h-4 w-4" />
            Add System
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredSystems.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No systems found</p>
        ) : (
          <div className="space-y-1">
            {filteredSystems.map(system => {
              const typeId = normalizeSystemType(system);
              const typeMeta = SYSTEM_TYPE_META.find(t => t.id === typeId) || SYSTEM_TYPE_META[0];
              const Icon = typeMeta.icon;
              const title = system.name || system.title || 'Untitled system';
              const updatedAt = system.updatedAt || system.updatedOn || system.lastUpdated || system.createdAt;
              return (
                <button
                  key={system.id}
                  onClick={() => onSelectSystem(system)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    selectedSystem?.id === system.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center bg-${typeMeta.color}-100 text-${typeMeta.color}-600`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{title}</p>
                    <p className="text-xs text-gray-500 truncate">{typeMeta.label}</p>
                  </div>
                  <div className="text-xs text-gray-400">
                    {updatedAt ? formatRelativeTime(updatedAt) : 'No updates'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const SystemsDetailPanel = ({ system, bookId, actors = [], items = [], activities = [], onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const [subsystems, setSubsystems] = useState([]);

  useEffect(() => { if (system) loadLogs(); }, [system]);
  useEffect(() => { if (system) loadSubsystems(); }, [system]);

  const loadLogs = async () => {
    try {
      const logsData = await schoolmateService.getLogs(bookId, { entityType: 'system', entityId: system.id, limit: 50 });
      const scopedLogs = logsData.filter(log => log.entityId === system.id);
      setLogs(scopedLogs);
    } catch (err) { console.error(err); }
  };

  const loadSubsystems = async () => {
    if (!system?.id) {
      setSubsystems([]);
      return;
    }
    try {
      const subsystemsData = await schoolmateService.getSystemSubsystems(bookId, system.id);
      setSubsystems(subsystemsData);
    } catch (err) { console.error(err); }
  };

  if (!system) return null;

  const typeId = normalizeSystemType(system);
  const typeMeta = SYSTEM_TYPE_META.find(t => t.id === typeId) || SYSTEM_TYPE_META[0];
  const TypeIcon = typeMeta.icon;
  const title = system.name || system.title || 'Untitled system';
  const owner = system.ownerName || actors.find(actor => actor.id === system.ownerId)?.name || system.leadName || system.owner || '';
  const start = system.startDate || system.start || system.startAt;
  const createdOn = system.createdOn || system.createdAt;
  const updatedOn = system.updatedOn || system.updatedAt;
  const validFrom = system.validFrom || system.validityStart || system.validityFrom;
  const validTo = system.validTo || system.validityEnd || system.validityTo;
  const utilisation = system.utilisation || system.utilization || system.envisagedUtilisation;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start gap-4 mb-3">
          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center bg-${typeMeta.color}-100 text-${typeMeta.color}-600`}>
            <TypeIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">{title}</h2>
            <p className="text-sm text-gray-500 truncate">{typeMeta.label}</p>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant={typeMeta.color}>{typeMeta.label}</Badge>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-sm">
          {owner && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <User className="h-3.5 w-3.5 text-gray-500" />
              {owner}
            </span>
          )}
          {(createdOn || updatedOn) && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Clock className="h-3.5 w-3.5 text-gray-500" />
              {createdOn ? `Created ${formatDate(createdOn)}` : 'Created date'}{updatedOn ? ` • Updated ${formatDate(updatedOn)}` : ''}
            </span>
          )}
          {(validFrom || validTo) && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {validFrom ? formatDate(validFrom) : 'Valid start'}{validTo ? ` → ${formatDate(validTo)}` : ''}
            </span>
          )}
          {start && !validFrom && !validTo && (
            <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-gray-500" />
              {formatDate(start)}
            </span>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200">
        {['overview', 'subsystems', 'logs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === tab ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab === 'overview' ? 'Overview' : tab === 'subsystems' ? 'Subsystems' : 'Logs'}
            {tab === 'logs' && logs.length > 0 && <Badge variant="blue" size="xs">{logs.length}</Badge>}
            {tab === 'subsystems' && subsystems.length > 0 && <Badge variant="blue" size="xs">{subsystems.length}</Badge>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-4 space-y-4">
            {system.description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Overview</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{system.description}</p>
              </div>
            )}
            {utilisation && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Envisaged Utilisation</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{utilisation}</p>
              </div>
            )}
            {(createdOn || updatedOn || validFrom || validTo) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Dates</div>
                  <div>{createdOn ? `Created: ${formatDate(createdOn)}` : 'Created: —'}</div>
                  <div>{updatedOn ? `Updated: ${formatDate(updatedOn)}` : 'Updated: —'}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Validity Period</div>
                  <div>{validFrom ? formatDate(validFrom) : 'Start: —'}</div>
                  <div>{validTo ? formatDate(validTo) : 'End: —'}</div>
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Subsystems</h4>
                {subsystems.length > 0 && <Badge variant="blue" size="xs">{subsystems.length}</Badge>}
              </div>
              {subsystems.length === 0 ? (
                <p className="text-sm text-gray-500">No subsystems added yet.</p>
              ) : (
                <div className="space-y-2">
                  {subsystems.slice(0, 3).map((subsystem) => (
                    <div key={subsystem.id} className="rounded-lg border border-gray-100 bg-white p-2">
                      <div className="text-sm font-medium text-gray-900">
                        {subsystem.name || subsystem.title || 'Untitled subsystem'}
                      </div>
                      {subsystem.description && (
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">{subsystem.description}</div>
                      )}
                    </div>
                  ))}
                  {subsystems.length > 3 && (
                    <p className="text-xs text-gray-500">See all subsystems in the Subsystems tab.</p>
                  )}
                </div>
              )}
            </div>
            {!system.description && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                Capture the purpose, scope, and key notes for this system.
              </div>
            )}
          </div>
        )}

        {activeTab === 'subsystems' && (
          <div className="p-4 space-y-4">
            {subsystems.length === 0 ? (
              <EmptyState icon={Sliders} title="No subsystems yet" description="Add subsystems to structure this system." />
            ) : (
              subsystems.map((subsystem) => {
                const subsystemItems = Array.isArray(subsystem.items) ? subsystem.items : [];
                const subsystemOwner = subsystem.ownerName || actors.find(actor => actor.id === subsystem.ownerId)?.name || '';
                return (
                  <div key={subsystem.id} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900">{subsystem.name || subsystem.title || 'Untitled subsystem'}</h4>
                        {subsystem.description && (
                          <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{subsystem.description}</p>
                        )}
                      </div>
                      {subsystem.status && (
                        <Badge variant="blue">{subsystem.status}</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-3">
                      {subsystemOwner && <Badge variant="slate">{subsystemOwner}</Badge>}
                      {(subsystem.startDate || subsystem.endDate) && (
                        <span>
                          {subsystem.startDate ? formatPlanDate(subsystem.startDate) : 'No start'}
                          {subsystem.endDate ? ` • ${formatPlanDate(subsystem.endDate)}` : ''}
                        </span>
                      )}
                    </div>
                    {subsystemItems.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-600 mb-2">Items</div>
                        <div className="flex flex-wrap gap-2">
                          {subsystemItems.map((item, idx) => (
                            <span key={item.id || `${subsystem.id}-item-${idx}`} className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                              {item.name || item.label || item.title || 'Untitled item'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="divide-y divide-gray-100">
            {logs.length === 0 ? (
              <EmptyState icon={FileText} title="No logs" description="System updates will appear here" />
            ) : (
              logs.map(log => <LogFeedItem key={log.id} log={log} />)
            )}
          </div>
        )}
      </div>

      <LogComposer
        bookId={bookId}
        entityType="system"
        entityId={system.id}
        entityName={title}
        referenceSources={{ actors, items, activities }}
        onLogCreated={loadLogs}
        placeholder={`Add a note about ${title}...`}
      />
    </div>
  );
};

// ============================================================
// ACTIVITIES LIST
// ============================================================

const ActivitiesSidebar = ({ activities, selectedType, onSelectType }) => {
  const counts = useMemo(() => {
    return activities.reduce((acc, activity) => {
      const type = activity.activityType || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      acc.all += 1;
      return acc;
    }, { all: 0 });
  }, [activities]);

  const filters = [
    { id: 'all', label: 'All', icon: ClipboardList },
    { id: ActivityType.TASK, label: ActivityTypeMeta[ActivityType.TASK]?.label || 'Task', icon: ClipboardList },
    { id: ActivityType.TODO, label: ActivityTypeMeta[ActivityType.TODO]?.label || 'To-Do', icon: ClipboardList },
    { id: ActivityType.MEETING, label: ActivityTypeMeta[ActivityType.MEETING]?.label || 'Meeting', icon: Users },
    { id: ActivityType.EVENT, label: ActivityTypeMeta[ActivityType.EVENT]?.label || 'Event', icon: Calendar },
    { id: ActivityType.PROJECT, label: ActivityTypeMeta[ActivityType.PROJECT]?.label || 'Project', icon: Target },
    { id: ActivityType.NOTE, label: ActivityTypeMeta[ActivityType.NOTE]?.label || 'Note', icon: FileText },
    { id: ActivityType.REFLECTION, label: ActivityTypeMeta[ActivityType.REFLECTION]?.label || 'Reflection', icon: TrendingUp },
    { id: ActivityType.CUSTOM, label: ActivityTypeMeta[ActivityType.CUSTOM]?.label || 'Custom', icon: Sliders }
  ];

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Activities</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filters.map(filter => {
          const Icon = filter.icon;
          const isActive = selectedType === filter.id || (!selectedType && filter.id === 'all');
          const count = counts[filter.id] || 0;
          return (
            <button
              key={filter.id}
              onClick={() => onSelectType?.(filter.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="flex-1 font-medium">{filter.label}</span>
              <span className="text-sm text-gray-400">{count}</span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-gray-200 p-4 text-xs text-gray-500 space-y-2">
        <div className="font-semibold text-gray-700">Activity types</div>
        <div>Use filters to focus on specific workstreams.</div>
      </div>
    </div>
  );
};

const ActivitiesListView = ({
  activities,
  selectedActivity,
  onSelectActivity,
  typeFilter: externalTypeFilter,
  onTypeFilterChange
}) => {
  const [search, setSearch] = useState('');
  const [localTypeFilter, setLocalTypeFilter] = useState('all');
  const typeFilter = externalTypeFilter ?? localTypeFilter;
  const setTypeFilter = onTypeFilterChange ?? setLocalTypeFilter;
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredActivities = useMemo(() => {
    let result = activities;
    if (typeFilter !== 'all') result = result.filter(a => a.activityType === typeFilter);
    if (statusFilter !== 'all') result = result.filter(a => a.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activities, search, typeFilter, statusFilter]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Activities</h2>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg bg-white">
            <option value="all">All types</option>
            {ActivityTypeOrder.map(type => (
              <option key={type} value={type}>{ActivityTypeMeta[type]?.label || type}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg bg-white">
            <option value="all">All status</option>
            {Object.entries(ActivityStatusMeta).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredActivities.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No activities found</p>
        ) : (
          <div className="space-y-1">
            {filteredActivities.map(activity => {
              const typeMeta = ActivityTypeMeta[activity.activityType] || { label: activity.activityType || 'Activity', color: 'slate' };
              const statusMeta = ActivityStatusMeta[activity.status] || { label: activity.status || 'Pending', color: 'slate' };
              const dateLabel = activity.startTime
                ? formatDateTime(activity.startTime)
                : activity.dueDate
                  ? formatDate(activity.dueDate)
                  : 'No date';
              return (
                <button
                  key={activity.id}
                  onClick={() => onSelectActivity(activity)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    selectedActivity?.id === activity.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{activity.name}</p>
                    <p className="text-xs text-gray-500 truncate">{dateLabel}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={typeMeta.color} size="xs">{typeMeta.label}</Badge>
                    <Badge variant={statusMeta.color} size="xs">{statusMeta.label}</Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// ITEM FORM MODAL
// ============================================================

const ItemFormModal = ({ open, item, actors, onClose, onSave }) => {
  const [form, setForm] = useState(item || { status: ItemStatus.AVAILABLE, quantity: 1 });

  useEffect(() => {
    setForm(item || { status: ItemStatus.AVAILABLE, quantity: 1 });
  }, [item, open]);

  if (!open) return null;

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.name?.trim()) return;
    const payload = {
      ...form,
      name: form.name.trim(),
      tags: typeof form.tags === 'string'
        ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
        : form.tags
    };
    onSave(payload);
  };

  const assignedActor = actors.find(a => a.id === form.assignedToId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">{form.id ? 'Edit Item' : 'New Item'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Item Name *</label>
            <input value={form.name || ''} onChange={(e) => handleChange('name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Projector, Science Lab" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <input value={form.category || ''} onChange={(e) => handleChange('category', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Equipment, Classroom" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status || ItemStatus.AVAILABLE} onChange={(e) => handleChange('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                {ITEM_STATUS_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input value={form.location || ''} onChange={(e) => handleChange('location', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Block A, Lab 2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Quantity</label>
              <input type="number" min="1" value={form.quantity || 1} onChange={(e) => handleChange('quantity', parseInt(e.target.value || '1', 10))} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Assigned To</label>
              <select
                value={form.assignedToId || ''}
                onChange={(e) => {
                  const actor = actors.find(a => a.id === e.target.value);
                  handleChange('assignedToId', e.target.value);
                  handleChange('assignedToName', actor?.name || '');
                }}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Unassigned</option>
                {actors.map(actor => (
                  <option key={actor.id} value={actor.id}>{actor.name}</option>
                ))}
              </select>
              {assignedActor && <p className="text-xs text-gray-500 mt-1">{getActorSubtitle(assignedActor)}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Serial Number</label>
              <input value={form.serialNumber || ''} onChange={(e) => handleChange('serialNumber', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Condition</label>
            <input value={form.condition || ''} onChange={(e) => handleChange('condition', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Good, Needs repair" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
            <input value={Array.isArray(form.tags) ? form.tags.join(', ') : (form.tags || '')} onChange={(e) => handleChange('tags', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. projector, grade-10" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea value={form.description || ''} onChange={(e) => handleChange('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} />
          </div>
        </form>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save Item</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ACTORS LIST
// ============================================================

const ActorsSidebar = ({ actors, groups, actorFilter, setActorFilter }) => (
  <div className="flex flex-col h-full min-h-0">
    <div className="p-4 border-b border-gray-200">
      <h2 className="text-lg font-bold text-gray-900">Actors</h2>
      <p className="text-xs text-gray-500 mt-1">Filter by type</p>
      <div className="mt-3 space-y-1">
        <button
          onClick={() => setActorFilter('all')}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${
            actorFilter === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span>All</span>
          <span className="text-xs text-gray-400">{actors.length}</span>
        </button>
        {ACTOR_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => setActorFilter(type.id)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${
              actorFilter === type.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span>{type.label}</span>
            <span className="text-xs text-gray-400">{actors.filter(a => normalizeActorType(a.type) === type.id).length}</span>
          </button>
        ))}
      </div>
    </div>

    <div className="flex-1 overflow-y-auto">
      <div className="px-4 py-3 border-b border-gray-100">
        {ACTOR_TYPES.map(type => (
          <StatCard key={type.id} icon={type.icon} label={type.label} value={actors.filter(a => normalizeActorType(a.type) === type.id).length} color={type.color} />
        ))}
      </div>

    <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Groups</h3>
        <div className="space-y-1">
          {groups.map(group => (
            <button key={group.id} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 text-left">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center"><UsersRound className="h-4 w-4 text-blue-600" /></div>
                <span className="text-sm font-medium">{group.name}</span>
              </div>
              <span className="text-sm text-gray-500">{group.memberCount || 0}</span>
            </button>
          ))}
          <button className="w-full flex items-center gap-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium">
            <Plus className="h-4 w-4" /> Add Group
          </button>
        </div>
      </div>
    </div>
  </div>
);

const ActorsListColumn = ({ actors, selectedActor, onSelectActor, actorFilter }) => {
  const [search, setSearch] = useState('');

  const filteredActors = useMemo(() => {
    let result = actors;
    if (actorFilter && actorFilter !== 'all') result = result.filter(a => normalizeActorType(a.type) === actorFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a => a.name?.toLowerCase().includes(q));
    }
    return result;
  }, [actors, actorFilter, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actors..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredActors.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No actors found</p>
        ) : (
          <div className="space-y-1">
            {filteredActors.map(actor => (
              <div
                key={actor.id}
                className={`group w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                  selectedActor?.id === actor.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                }`}
              >
                <button onClick={() => onSelectActor(actor)} className="flex items-center gap-3 w-full text-left">
                  <Avatar name={actor.name} size="sm" src={actor.photoUrl} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{actor.name}</p>
                    <p className="text-xs text-gray-500">{getActorSubtitle(actor)}</p>
                  </div>
                  <span className="ml-autotext-xs text-gray-400">{formatRelativeTime(actor.lastActivity)}</span>
                </button>
                <div className="flex items-center gap-1 transition-opacity">
                  <button
                    className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600"
                    title="Call"
                    onClick={() => actor.phone && window.open(`tel:${actor.phone}`)}
                  >
                    <Phone className="h-4 w-4" />
                  </button>
                  <button
                    className="p-1.5 rounded-lg hover:bg-indigo-100 text-indigo-600"
                    title="Message"
                    onClick={() => actor.email && window.open(`mailto:${actor.email}`)}
                  >
                    <Mail className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
        {Math.min(10, filteredActors.length)} of {filteredActors.length}
        <div className="inline-flex gap-1 ml-4">
          <button className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="h-4 w-4" /></button>
          <button className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

const ActorsListView = ({ actors, groups, selectedActor, onSelectActor, actorFilter, setActorFilter }) => {
  const [search, setSearch] = useState('');

  const filteredActors = useMemo(() => {
    let result = actors;
    if (actorFilter && actorFilter !== 'all') result = result.filter(a => normalizeActorType(a.type) === actorFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a => a.name?.toLowerCase().includes(q));
    }
    return result;
  }, [actors, actorFilter, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Actors</h2>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-b border-gray-100">
        {ACTOR_TYPES.map(type => (
          <StatCard key={type.id} icon={type.icon} label={type.label} value={actors.filter(a => normalizeActorType(a.type) === type.id).length} color={type.color} />
        ))}
      </div>

      {/* Groups */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Groups</h3>
        <div className="space-y-1">
          {groups.map(group => (
            <button key={group.id} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 text-left">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center"><UsersRound className="h-4 w-4 text-blue-600" /></div>
                <span className="text-sm font-medium">{group.name}</span>
              </div>
              <span className="text-sm text-gray-500">{group.memberCount || 0}</span>
            </button>
          ))}
          <button className="w-full flex items-center gap-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium">
            <Plus className="h-4 w-4" /> Add Group
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2">
        {filteredActors.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No actors found</p>
        ) : (
          <div className="space-y-1">
            {filteredActors.map(actor => (
              <button
                key={actor.id}
                onClick={() => onSelectActor(actor)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                  selectedActor?.id === actor.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                }`}
              >
                <Avatar name={actor.name} size="sm" src={actor.photoUrl} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{actor.name}</p>
                  <p className="text-xs text-gray-500">{getActorSubtitle(actor)}</p>
                </div>
                <span className="text-xs text-gray-400">{formatRelativeTime(actor.lastActivity)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
        1–{Math.min(10, filteredActors.length)} of {filteredActors.length}
        <div className="inline-flex gap-1 ml-4">
          <button className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="h-4 w-4" /></button>
          <button className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DASHBOARD VIEW
// ============================================================

const DashboardView = ({ stats, recentLogs, activities, schedule, onActorClick, actorCounts, items }) => {
  const totalActors = stats?.actors?.total ?? 0;
  const learners = (actorCounts?.learner ?? stats?.actors?.students) || 0;
  const staff = (actorCounts?.staff ?? stats?.actors?.teachers) || 0;
  const groups = stats?.classes || 0;
  const subjects = stats?.subjects || 0;
  const itemsTotal = items?.length || 0;
  const assessments = stats?.assessments?.total || 0;
  const tasksTotal = stats?.tasks?.total ?? activities?.length ?? 0;
  const tasksPending = stats?.tasks?.pending || 0;
  const logsTotal = stats?.logs ? Object.values(stats.logs).reduce((sum, value) => sum + (value || 0), 0) : (recentLogs?.length || 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
      {/* Left 2/3 - Snapshot, Activities, Timetable */}
      <div className="lg:col-span-8 space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Aggregate Snapshot</h3>
              <p className="text-sm text-gray-500">Wide-angle totals across people, assets, and activity.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-2">
            <StatCard icon={Users} label="Total People" value={totalActors} color="blue" />
            <StatCard icon={GraduationCap} label="Learners" value={learners} color="blue" />
            <StatCard icon={Briefcase} label="Staff" value={staff} color="indigo" />
            <StatCard icon={UsersRound} label="Groups" value={groups} color="purple" />
            <StatCard icon={BookOpen} label="Subjects" value={subjects} color="emerald" />
            <StatCard icon={Box} label="Inventory Items" value={itemsTotal} color="amber" />
            <StatCard icon={Award} label="Assessments" value={assessments} color="violet" />
            <StatCard icon={Target} label="Tasks" value={tasksTotal} color="teal" />
            <StatCard icon={Clock} label="Tasks Pending" value={tasksPending} color="orange" />
            <StatCard icon={Calendar} label="Today's Lessons" value={schedule?.length || 0} color="green" />
            <StatCard icon={ClipboardList} label="Log Entries" value={logsTotal} color="slate" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activities */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Upcoming Activities</h3>
              <button className="text-blue-600 text-sm hover:underline flex items-center gap-1">View all <ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="divide-y divide-gray-50">
              {activities?.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 text-center">No upcoming activities</p>
              ) : (
                activities?.slice(0, 6).map(activity => {
                  const activityMeta = ACTIVITY_TYPE_OPTIONS.find(option => option.id === activity.activityType);
                  const activityDot = ACTIVITY_COLOR_CLASSES[activityMeta?.color] || 'bg-blue-500';
                  return (
                    <div key={activity.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-2 w-2 rounded-full ${activityDot}`} />
                        <div>
                          <p className="font-medium text-gray-900">{activity.name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>{activity.dueDate ? formatDate(activity.dueDate) : 'No due date'}</span>
                            {activityMeta && <Badge variant={activityMeta.color}>{activityMeta.label}</Badge>}
                          </div>
                        </div>
                      </div>
                      <button className="p-1 hover:bg-gray-100 rounded"><MoreHorizontal className="h-4 w-4 text-gray-400" /></button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Timetable */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Timetable</h3>
              <select className="text-sm border border-gray-200 rounded-lg px-2 py-1"><option>Today</option></select>
            </div>
            <div className="p-4 space-y-2">
              {schedule?.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No lessons scheduled</p>
              ) : (
                schedule?.slice(0, 6).map((slot, idx) => (
                  <div key={slot.id || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <div className={`w-1 h-10 rounded-full ${
                      slot.lesson?.status === 'completed' ? 'bg-green-500' : slot.lesson?.status === 'in-progress' ? 'bg-amber-500' : 'bg-blue-200'
                    }`} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{slot.subjectName}</p>
                      <p className="text-xs text-gray-500">{slot.className} • P{slot.period}</p>
                    </div>
                    <span className="text-xs text-gray-400">{slot.startTime}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right 1/3 - Recent Logs */}
      <div className="lg:col-span-4">
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ maxHeight: '80vh' }}>
          <div className="p-4 border-b border-gray-100 shrink-0"><h3 className="font-semibold text-gray-900">Recent Logs</h3></div>
          <div className="overflow-y-auto divide-y divide-gray-50 flex-1 min-h-0 mb-10">
            {recentLogs?.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">No recent activity</p>
            ) : (
              recentLogs?.map(log => <LogFeedItem key={log.id} log={log} onActorClick={onActorClick} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MOBILE VIEW
// ============================================================

const MobileView = ({
  view,
  stats,
  recentLogs,
  actors,
  selectedActor,
  setSelectedActor,
  items,
  selectedItem,
  setSelectedItem,
  plans,
  selectedPlan,
  setSelectedPlan,
  systems,
  selectedSystem,
  setSelectedSystem,
  activities,
  selectedActivity,
  setSelectedActivity,
  bookId,
  onRefresh,
  actorFilter,
  setActorFilter,
  actorCounts,
  onActorCategoryAdded,
  onItemCategoryAdded,
  classes,
  scheduleSlots
}) => {
  const totalActors = stats?.actors?.total ?? 0;
  const learners = (actorCounts?.learner ?? stats?.actors?.students) || 0;
  const staff = (actorCounts?.staff ?? stats?.actors?.teachers) || 0;
  const groups = stats?.classes || 0;
  const subjects = stats?.subjects || 0;
  const itemsTotal = items?.length || 0;
  const assessments = stats?.assessments?.total || 0;
  const tasksTotal = stats?.tasks?.total ?? activities?.length ?? 0;
  const tasksPending = stats?.tasks?.pending || 0;
  const logsTotal = stats?.logs ? Object.values(stats.logs).reduce((sum, value) => sum + (value || 0), 0) : (recentLogs?.length || 0);

  if (view === 'actors' && selectedActor) {
    return (
      <ActorDetailPanel
        actor={selectedActor}
        bookId={bookId}
        actors={actors}
        classes={classes}
        scheduleSlots={scheduleSlots}
        items={items}
        activities={activities}
        onClose={() => setSelectedActor(null)}
        onRefresh={onRefresh}
        onCategoryAdded={onActorCategoryAdded}
      />
    );
  }

  if (view === 'items' && selectedItem) {
    return (
      <ItemDetailPanel
        item={selectedItem}
        bookId={bookId}
        actors={actors}
        items={items}
        activities={activities}
        onClose={() => setSelectedItem(null)}
        onRefresh={onRefresh}
        onEdit={() => {}}
        onCategoryAdded={onItemCategoryAdded}
      />
    );
  }

  if (view === 'planner' && selectedPlan) {
    return (
      <PlannerDetailPanel
        plan={selectedPlan}
        bookId={bookId}
        actors={actors}
        items={items}
        activities={activities}
        onClose={() => setSelectedPlan(null)}
        onRefresh={onRefresh}
      />
    );
  }

  if (view === 'systems' && selectedSystem) {
    return (
      <SystemsDetailPanel
        system={selectedSystem}
        bookId={bookId}
        actors={actors}
        items={items}
        activities={activities}
        onClose={() => setSelectedSystem(null)}
        onRefresh={onRefresh}
      />
    );
  }

  if (view === 'activities' && selectedActivity) {
    return (
      <ActivityDetailPanel
        activity={selectedActivity}
        bookId={bookId}
        actors={actors}
        items={items}
        activities={activities}
        onClose={() => setSelectedActivity(null)}
        onRefresh={onRefresh}
      />
    );
  }

  if (view === 'actors') {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold">Actors</h1>
            <button className="p-2 hover:bg-gray-100 rounded-lg"><Search className="h-5 w-5 text-gray-600" /></button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {['all', ...ACTOR_TYPES.map(t => t.id)].map(type => (
              <button
                key={type}
                onClick={() => setActorFilter(type)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${
                  actorFilter === type ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {type === 'all' ? 'All' : ACTOR_TYPES.find(t => t.id === type)?.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {actors.filter(a => actorFilter === 'all' || normalizeActorType(a.type) === actorFilter).map(actor => (
            <button key={actor.id} onClick={() => setSelectedActor(actor)} className="w-full flex items-center gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 text-left">
              <Avatar name={actor.name} size="md" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{actor.name}</p>
                <p className="text-sm text-gray-500">{getActorSubtitle(actor)}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (view === 'items') {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold">Items</h1>
          </div>
          <p className="text-sm text-gray-500">Track resources, equipment, spaces, and documents.</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <EmptyState icon={Box} title="No items yet" description="Create your first item to start tracking usage." />
          ) : (
            items.map(item => {
              const statusMeta = getItemStatusMeta(item.status);
              return (
                <button key={item.id} onClick={() => setSelectedItem(item)} className="w-full flex items-center gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 text-left">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Box className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">{item.category || 'General item'} • {item.location || 'No location'}</p>
                  </div>
                  <Badge variant={statusMeta.color} size="xs">{statusMeta.label}</Badge>
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  if (view === 'planner') {
    return (
      <PlannerListView
        plans={plans}
        selectedPlan={selectedPlan}
        onSelectPlan={setSelectedPlan}
      />
    );
  }

  if (view === 'systems') {
    return (
      <SystemsListView
        systems={systems}
        selectedSystem={selectedSystem}
        onSelectSystem={setSelectedSystem}
      />
    );
  }

  if (view === 'activities') {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold">Activities</h1>
          </div>
          <p className="text-sm text-gray-500">Tasks, meetings, events, and notes.</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {activities.length === 0 ? (
            <EmptyState icon={Calendar} title="No activities yet" description="Create your first activity to start tracking work." />
          ) : (
            activities.map(activity => {
              const typeMeta = ActivityTypeMeta[activity.activityType] || { label: activity.activityType || 'Activity', color: 'slate' };
              const statusMeta = ActivityStatusMeta[activity.status] || { label: activity.status || 'Pending', color: 'slate' };
              return (
                <button key={activity.id} onClick={() => setSelectedActivity(activity)} className="w-full flex items-center gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 text-left">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{activity.name}</p>
                    <p className="text-sm text-gray-500 truncate">{activity.startTime ? formatDateTime(activity.startTime) : activity.dueDate ? formatDate(activity.dueDate) : 'No date'}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant={typeMeta.color} size="xs">{typeMeta.label}</Badge>
                      <Badge variant={statusMeta.color} size="xs">{statusMeta.label}</Badge>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // Dashboard mobile
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 bg-white border-b border-gray-200">
        <h1 className="text-xl font-bold mb-3">Dashboard</h1>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full whitespace-nowrap">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">People: {totalActors}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 rounded-full whitespace-nowrap">
            <GraduationCap className="h-4 w-4 text-sky-600" />
            <span className="text-sm font-medium text-sky-700">Learners: {learners}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full whitespace-nowrap">
            <Briefcase className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">Staff: {staff}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full whitespace-nowrap">
            <UsersRound className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Groups: {groups}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full whitespace-nowrap">
            <BookOpen className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Subjects: {subjects}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full whitespace-nowrap">
            <Box className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Items: {itemsTotal}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-full whitespace-nowrap">
            <Target className="h-4 w-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">Tasks: {tasksTotal}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full whitespace-nowrap">
            <Clock className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">Pending: {tasksPending}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-50 rounded-full whitespace-nowrap">
            <Award className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">Assessments: {assessments}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full whitespace-nowrap">
            <ClipboardList className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-medium text-slate-700">Logs: {logsTotal}</span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="p-4"><h2 className="font-semibold text-gray-900 mb-3">Recent Logs</h2></div>
        <div className="divide-y divide-gray-100">{recentLogs?.map(log => <LogFeedItem key={log.id} log={log} />)}</div>
      </div>
      <LogComposer
        bookId={bookId}
        referenceSources={{ actors, items, activities }}
        onLogCreated={onRefresh}
      />
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const SchoolmateTemplate = ({ bookId, book = {}, bookData = {}, styling = {}, viewManager }) => {
  const { viewMode, hasManagementAccess } = viewManager || { viewMode: 'public', hasManagementAccess: false };
  const { user, userProfile, logout } = useAuth();
  const effectiveBookData = bookData || book;
  const [activeView, setActiveView] = useState('dashboard');
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [actors, setActors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activities, setActivities] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [systems, setSystems] = useState([]);

  const [selectedActor, setSelectedActor] = useState(null);
  const [actorFilter, setActorFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemCategoryFilter, setItemCategoryFilter] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planTypeFilter, setPlanTypeFilter] = useState('all');
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [systemTypeFilter, setSystemTypeFilter] = useState('all');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const actorCounts = useMemo(() => {
    const counts = { learner: 0, staff: 0, sgb: 0, stakeholder: 0 };
    actors.forEach(actor => {
      const type = normalizeActorType(actor.type);
      if (counts[type] !== undefined) counts[type] += 1;
    });
    return counts;
  }, [actors]);

  useEffect(() => { loadData(); }, [bookId, refreshKey]);
  useEffect(() => {
    if (activeView === 'actors' && !selectedActor && actors.length > 0) {
      setSelectedActor(actors[0]);
    }
  }, [activeView, actors, selectedActor]);
  useEffect(() => {
    if (activeView === 'activities' && !selectedActivity && activities.length > 0) {
      setSelectedActivity(activities[0]);
    }
  }, [activeView, activities, selectedActivity]);
  useEffect(() => {
    if (activeView === 'items' && !selectedItem && items.length > 0) {
      setSelectedItem(items[0]);
    }
  }, [activeView, items, selectedItem]);
  useEffect(() => {
    if (activeView === 'planner' && !selectedPlan && plans.length > 0) {
      setSelectedPlan(plans[0]);
    }
  }, [activeView, plans, selectedPlan]);
  useEffect(() => {
    if (activeView === 'systems' && !selectedSystem && systems.length > 0) {
      setSelectedSystem(systems[0]);
    }
  }, [activeView, systems, selectedSystem]);

  const loadData = async () => {
    setLoading(true);
    try {
      await schoolmateService.migrateActivityTypes(bookId);
      const [statsData, logsData, actorsData, classesData, activitiesData, scheduleData, slotsData, itemsData, plansData, systemsData] = await Promise.all([
        schoolmateService.getStats(bookId),
        schoolmateService.getActivityFeed(bookId, 50),
        schoolmateService.getActors(bookId),
        schoolmateService.getClasses(bookId),
        schoolmateService.getActivities(bookId),
        schoolmateService.getTodaySchedule(bookId),
        schoolmateService.getScheduleSlots(bookId),
        schoolmateService.getItems(bookId),
        schoolmateService.getPlannerPlans(bookId),
        schoolmateService.getSystems(bookId)
      ]);
      setStats(statsData);
      setRecentLogs(logsData);
      setActors(actorsData);
      setGroups(classesData);
      setActivities(activitiesData);
      setSchedule(scheduleData);
      setScheduleSlots(slotsData);
      setItems(itemsData);
      setPlans(plansData);
      setSystems(systemsData);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleRefresh = () => setRefreshKey(k => k + 1);
  const handleNavigate = (view) => {
    setActiveView(view);
    setMoreMenuOpen(false);
  };
  const handleActorClick = (actorId) => {
    const actor = actors.find(a => a.id === actorId);
    if (actor) { setSelectedActor(actor); setActiveView('actors'); }
  };
  const handleActorCategoryAdded = (actorId, category) => {
    setActors(prev => prev.map(actor => {
      if (actor.id !== actorId) return actor;
      const nextCategories = Array.from(new Set([...(actor.logCategories || []), category]));
      return { ...actor, logCategories: nextCategories };
    }));
    setSelectedActor(prev => {
      if (!prev || prev.id !== actorId) return prev;
      const nextCategories = Array.from(new Set([...(prev.logCategories || []), category]));
      return { ...prev, logCategories: nextCategories };
    });
  };
  const handleItemCategoryAdded = (itemId, category) => {
    setItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const nextCategories = Array.from(new Set([...(item.logCategories || []), category]));
      return { ...item, logCategories: nextCategories };
    }));
    setSelectedItem(prev => {
      if (!prev || prev.id !== itemId) return prev;
      const nextCategories = Array.from(new Set([...(prev.logCategories || []), category]));
      return { ...prev, logCategories: nextCategories };
    });
  };

  if (viewMode === 'management' && hasManagementAccess) {
    return (
      <SchoolmateManager
        bookId={bookId}
        book={bookData || book}
        onClose={() => {
          if (viewManager?.toggleView) {
            viewManager.toggleView();
          }
        }}
      />
    );
  }

  if (loading && recentLogs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isOwner = user?.uid && effectiveBookData?.ownerId && user.uid === effectiveBookData.ownerId;
  const headerSettings = effectiveBookData?.headerSettings || {};
  const headerHeight = headerSettings.headerHeight ?? 80;
  const logoHeight = headerSettings.logoHeight ?? 40;
  const showLogo = headerSettings.showLogo ?? true;
  const showTitle = headerSettings.showTitle ?? true;
  const showDescription = headerSettings.showDescription ?? false;
  const headerPosition = headerSettings.position || 'sticky';
  const headerPositionClass = headerPosition === 'fixed'
    ? 'fixed top-0 left-0 right-0'
    : headerPosition === 'static'
      ? 'relative'
      : 'sticky top-0';

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header
        className={`${headerPositionClass} z-40 border-b border-gray-200`}
        style={{ backgroundColor: styling.headerBackgroundColor || '#ffffff' }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-6 flex-wrap" style={{ minHeight: `${headerHeight}px` }}>
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              {showLogo && effectiveBookData?.logoUrl && (
                <img
                  src={effectiveBookData.logoUrl}
                  alt={`${effectiveBookData?.botName || 'Schoolmate'} logo`}
                  className="w-auto rounded-lg object-contain"
                  style={{
                    height: `${logoHeight}px`,
                    filter: `invert(${effectiveBookData?.styling?.logoInvert || 0}%)`
                  }}
                />
              )}
              {showLogo && !effectiveBookData?.logoUrl && (
                <GraduationCap className="h-7 w-7" style={{ color: styling.primaryColor || '#2563eb' }} />
              )}
              {showTitle && (
                <div className="min-w-0">
                  <span
                    className="block text-lg font-bold truncate"
                    style={{ color: styling.headerColor || '#1e293b' }}
                  >
                    {effectiveBookData?.botName || 'Schoolmate'}
                  </span>
                  {showDescription && effectiveBookData?.description && (
                    <span className="block text-xs text-gray-500 truncate">
                      {effectiveBookData.description}
                    </span>
                  )}
                </div>
              )}
            </div>
            <nav className="hidden lg:flex flex-1 flex-wrap items-center justify-center gap-1 min-w-0">
              {MAIN_NAV.map(nav => (
                <button
                  key={nav.id}
                  onClick={() => handleNavigate(nav.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${activeView === nav.id ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {nav.label}
                </button>
              ))}
            </nav>
            <div className="flex items-center gap-2 flex-shrink-0">
              <UserProfile
                user={user}
                userProfile={user}
                styling={styling}
                bookData={{ ...effectiveBookData, headerSettings }}
                isOwner={isOwner}
                logout={logout}
                setShowAuthModal={setShowAuthModal}
                setShowProfileModal={setShowProfileModal}
                setMobileMenuOpen={() => {}}
                shouldShowUserProfile={() => true}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex min-h-0 overflow-hidden">
        <div className="hidden lg:flex flex-1">
          {activeView === 'dashboard' && <DashboardView stats={stats} recentLogs={recentLogs} activities={activities} schedule={schedule} onActorClick={handleActorClick} actorCounts={actorCounts} items={items} />}
          {activeView === 'actors' && (
            <div className="flex flex-1 min-h-0 overflow-hidden">
              <div className="w-72 border-r border-gray-200 bg-white min-h-0 overflow-hidden">
                <ActorsSidebar actors={actors} groups={groups} actorFilter={actorFilter} setActorFilter={setActorFilter} />
              </div>
              <div className="w-96 border-r border-gray-200 bg-white min-h-0 overflow-hidden">
                <ActorsListColumn actors={actors} selectedActor={selectedActor} onSelectActor={setSelectedActor} actorFilter={actorFilter} />
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                {selectedActor ? (
                  <ActorDetailPanel
                    actor={selectedActor}
                    bookId={bookId}
                    actors={actors}
                    classes={groups}
                    scheduleSlots={scheduleSlots}
                    items={items}
                    activities={activities}
                    onClose={() => setSelectedActor(null)}
                    onRefresh={handleRefresh}
                    onCategoryAdded={handleActorCategoryAdded}
                  />
                ) : (
                  <EmptyState icon={Users} title="Select an actor" description="Choose someone from the list" />
                )}
              </div>
            </div>
          )}
          {activeView === 'items' && (
            <div className="flex flex-1">
              <div className="w-80 border-r border-gray-200 bg-white">
                <ItemsSidebar
                  items={items}
                  selectedCategory={itemCategoryFilter}
                  onSelectCategory={setItemCategoryFilter}
                />
              </div>
              <div className="w-[420px] border-r border-gray-200 bg-white">
                <ItemsListView
                  items={items}
                  selectedItem={selectedItem}
                  onSelectItem={setSelectedItem}
                  categoryFilter={itemCategoryFilter}
                  onCategoryFilterChange={setItemCategoryFilter}
                />
              </div>
              <div className="flex-1">
                {selectedItem ? (
                  <ItemDetailPanel
                    item={selectedItem}
                    bookId={bookId}
                    actors={actors}
                    items={items}
                    activities={activities}
                    onClose={() => setSelectedItem(null)}
                    onRefresh={handleRefresh}
                    onEdit={() => {}}
                    onCategoryAdded={handleItemCategoryAdded}
                  />
                ) : (
                  <EmptyState icon={Box} title="Select an item" description="Choose an item to see details and logs" />
                )}
              </div>
            </div>
          )}
          {activeView === 'planner' && (
            <div className="flex flex-1 min-h-0">
              <div className="w-64 border-r border-gray-200 bg-white min-h-0">
                <PlannerSidebar
                  plans={plans}
                  selectedType={planTypeFilter}
                  onSelectType={setPlanTypeFilter}
                />
              </div>
              <div className="w-[420px] border-r border-gray-200 bg-white min-h-0">
                <PlannerListView
                  plans={plans}
                  selectedPlan={selectedPlan}
                  onSelectPlan={setSelectedPlan}
                  typeFilter={planTypeFilter}
                  onTypeFilterChange={setPlanTypeFilter}
                />
              </div>
              <div className="flex-1 min-h-0">
                {selectedPlan ? (
                  <PlannerDetailPanel
                    plan={selectedPlan}
                    bookId={bookId}
                    actors={actors}
                    items={items}
                    activities={activities}
                    onClose={() => setSelectedPlan(null)}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <EmptyState icon={Target} title="Select a plan" description="Choose a plan to see details and logs" />
                )}
              </div>
            </div>
          )}
          {activeView === 'activities' && (
            <div className="flex flex-1">
              <div className="w-80 border-r border-gray-200 bg-white">
                <ActivitiesSidebar
                  activities={activities}
                  selectedType={activityTypeFilter}
                  onSelectType={setActivityTypeFilter}
                />
              </div>
              <div className="w-[420px] border-r border-gray-200 bg-white">
                <ActivitiesListView
                  activities={activities}
                  selectedActivity={selectedActivity}
                  onSelectActivity={setSelectedActivity}
                  typeFilter={activityTypeFilter}
                  onTypeFilterChange={setActivityTypeFilter}
                />
              </div>
              <div className="flex-1">
                {selectedActivity ? (
                  <ActivityDetailPanel
                    activity={selectedActivity}
                    bookId={bookId}
                    actors={actors}
                    items={items}
                    activities={activities}
                    onClose={() => setSelectedActivity(null)}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <EmptyState icon={Calendar} title="Select an activity" description="Choose an activity to see details and logs" />
                )}
              </div>
            </div>
          )}
          {activeView === 'timetable' && <div className="flex-1 p-6"><EmptyState icon={Grid3X3} title="Timetable" description="Schedule management" /></div>}
          {activeView === 'marklists' && <div className="flex-1 p-6"><EmptyState icon={ClipboardList} title="Marklists" description="Assessment records" /></div>}
          {activeView === 'systems' && (
            <div className="flex flex-1 min-h-0">
              <div className="w-64 border-r border-gray-200 bg-white min-h-0">
                <SystemsSidebar
                  systems={systems}
                  selectedType={systemTypeFilter}
                  onSelectType={setSystemTypeFilter}
                />
              </div>
              <div className="w-[420px] border-r border-gray-200 bg-white min-h-0">
                <SystemsListView
                  systems={systems}
                  selectedSystem={selectedSystem}
                  onSelectSystem={setSelectedSystem}
                  typeFilter={systemTypeFilter}
                  onTypeFilterChange={setSystemTypeFilter}
                />
              </div>
              <div className="flex-1 min-h-0">
                {selectedSystem ? (
                  <SystemsDetailPanel
                    system={selectedSystem}
                    bookId={bookId}
                    actors={actors}
                    items={items}
                    activities={activities}
                    onClose={() => setSelectedSystem(null)}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <EmptyState icon={Sliders} title="Select a system" description="Choose a system to see details and logs" />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lg:hidden flex-1 pb-16">
          <MobileView
            view={activeView}
            stats={stats}
            recentLogs={recentLogs}
            actors={actors}
            selectedActor={selectedActor}
            setSelectedActor={setSelectedActor}
            items={items}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            plans={plans}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            systems={systems}
            selectedSystem={selectedSystem}
            setSelectedSystem={setSelectedSystem}
            activities={activities}
            selectedActivity={selectedActivity}
            setSelectedActivity={setSelectedActivity}
            bookId={bookId}
            onRefresh={handleRefresh}
            actorFilter={actorFilter}
            setActorFilter={setActorFilter}
            actorCounts={actorCounts}
            onActorCategoryAdded={handleActorCategoryAdded}
            onItemCategoryAdded={handleItemCategoryAdded}
            classes={groups}
            scheduleSlots={scheduleSlots}
          />
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex justify-around py-2">
          {MOBILE_NAV.map(nav => {
            const Icon = nav.icon;
            const isActive = activeView === nav.id || (nav.id === 'more' && moreMenuOpen);
            if (nav.id === 'more') {
              return (
                <button key={nav.id} onClick={() => setMoreMenuOpen(!moreMenuOpen)} className={`flex flex-col items-center gap-0.5 px-3 py-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                  <Icon className="h-6 w-6" /><span className="text-[10px] font-medium">{nav.label}</span>
                </button>
              );
            }
            return (
              <button key={nav.id} onClick={() => { handleNavigate(nav.id); setSelectedActor(null); }} className={`flex flex-col items-center gap-0.5 px-3 py-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                <Icon className="h-6 w-6" /><span className="text-[10px] font-medium">{nav.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Menu */}
      {moreMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMoreMenuOpen(false)} />
          <div className="absolute bottom-16 left-4 right-4 bg-white rounded-xl shadow-xl overflow-hidden">
            {MAIN_NAV.filter(n => !MOBILE_NAV.find(m => m.id === n.id)).map(nav => {
              const Icon = nav.icon;
              return (
                <button key={nav.id} onClick={() => handleNavigate(nav.id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
                  <Icon className="h-5 w-5 text-gray-500" /><span className="font-medium">{nav.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode="login"
        />
      )}

      {showProfileModal && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
          onSave={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
};

export default SchoolmateTemplate;
